import { PlusOutlined } from "@ant-design/icons";
import { faClockRotateLeft, faEye, faPencil, faTable, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Switch, Table, Tooltip } from "antd";
import { ProhibitedArea } from "components";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;

const SystemConnectionList = ({
    connectionList,
    onAddConnection,
    onViewConnection,
    onEditConnection,
    onActivateDeactivateConnection,
    onViewConnectionHistory,
    onViewConnectionTables,
    onDeleteConnection,
    permission,
}) => {
    return !permission ? (
        <ProhibitedArea></ProhibitedArea>
    ) : (
        <FlexContainerLayout
            footer={
                <Button type="primary" disabled={!permission.canAdd} onClick={onAddConnection} data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_ADD_BUTTON}>
                    <PlusOutlined /> Add Connection
                </Button>
            }
        >
            <Table
                size="small"
                rowKey="connectionId"
                dataSource={connectionList}
                pagination={false}
                className="flex size-full flex-1 flex-col [&_.error-row>td]:!border-inherit [&_.error-row>td]:![color:red] [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-header-footer [&_thead]:text-header-footer"
                data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_TABLE}
            >
                <Column
                    width="100px"
                    render={(value, connection, index) => (
                        <div className="flex gap-2">
                            <Tooltip title="Edit System Connection ">
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    className={permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canEdit ? onEditConnection(connection.connectionId, connection.type) : null)}
                                    data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_EDIT_BUTTON}
                                />
                            </Tooltip>
                            <Tooltip title="View System Connection ">
                                <FontAwesomeIcon
                                    icon={faEye}
                                    className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canView ? onViewConnection(connection.connectionId, connection.type) : null)}
                                    data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_VIEW_BUTTON}
                                />
                            </Tooltip>
                            {connection.type === "snowflake" && (
                                <Tooltip title="View Tables & Schema">
                                    <FontAwesomeIcon
                                        icon={faTable}
                                        className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                        onClick={() => (permission.canView ? onViewConnectionTables(connection.connectionId, connection.type) : null)}
                                        data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_TABLES_BUTTON}
                                    />
                                </Tooltip>
                            )}
                            <Tooltip title="View System Connection History">
                                <FontAwesomeIcon
                                    icon={faClockRotateLeft}
                                    className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canView ? onViewConnectionHistory(connection.connectionId, connection.type) : null)}
                                    data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_HISTORY_BUTTON}
                                />
                            </Tooltip>
                        </div>
                    )}
                />
                <Column
                    width="15%"
                    dataIndex="name"
                    title="Name"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    width="15%"
                    dataIndex="description"
                    title="Description"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    dataIndex="type"
                    title="Type"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => {
                        let displayValue = "";
                        if (value === "aws_s3") {
                            displayValue = "AWS S3 Connection";
                        } else if (value === "jdbc") {
                            displayValue = "PostgreSQL Connection";
                        } else if (value === "snowflake") {
                            displayValue = "Snowflake Connection";
                        } else if (value === "salesforce") {
                            displayValue = "Salesforce Connection";
                        } else if (value === "hubspot") {
                            displayValue = "HubSpot Connection";
                        }
                        return (
                            <Tooltip placement="topLeft" title={displayValue}>
                                {displayValue}
                            </Tooltip>
                        );
                    }}
                />
                <Column
                    width="10%"
                    dataIndex="businessAreaCount"
                    title="Business Areas"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => (
                        <Tooltip placement="topLeft" title={value || 0}>
                            {value || 0}
                        </Tooltip>
                    )}
                />
                <Column
                    dataIndex="createdDate"
                    title="Created Date"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    dataIndex="updatedDate"
                    title="Updated Date"
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    width="8%"
                    dataIndex="active"
                    title={"Active"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, connection, index) => {
                        return (
                            <Switch
                                checked={value}
                                disabled={!permission.canEdit}
                                onClick={(checked, event) => onActivateDeactivateConnection(connection, checked, connection.type)}
                                data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_ACTIVE_SWITCH}
                            ></Switch>
                        );
                    }}
                />
                <Column
                    width="5%"
                    render={(value, connection, index) => {
                        return (
                            <Tooltip title="Delete System Connection">
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    className={permission.canDelete ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => (permission.canDelete ? onDeleteConnection(connection) : null)}
                                    data-testid={CypressTestIds.SYSTEM_CONNECTION_LIST_DELETE_BUTTON}
                                />{" "}
                            </Tooltip>
                        );
                    }}
                />
            </Table>
        </FlexContainerLayout>
    );
};

export default SystemConnectionList;
